import React from 'react'

// Local dev overrides so cross-site links resolve against the running localhost host.
export const LOCALHOST_COUNTRY_URLS = {
    fr_CA: 'http://localhost:3000/fr-ca/',
    en_CA: 'http://localhost:3000/ca/',
    en_US: 'http://localhost:3000/us/',
    en_GB: 'http://localhost:3000/uk/'
}

export const isCanceledOrderStatus = (status) =>
    ['canceled', 'cancelled'].includes(status?.toLowerCase())

export function getSiteIdByLocaleId(sites, localeID) {
    for (const site of sites) {
        for (const locale of site.l10n.supportedLocales) {
            if (locale?.id.toLowerCase() === localeID.toLowerCase()) {
                return site.id
            }
        }
    }
    return null // Return null if no matching site ID is found
}

export function formatDate(dateString, locale = 'en-GB') {
    if (!dateString) {
        return
    }

    const [month, day, year] = dateString.split('/');

    const fullYear = year.length === 2 ? `20${year}` : year;

    const date = new Date(fullYear, month - 1, day);

    return date.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    });
}

// Intl.DisplayNames throws RangeError on anything but a 2-letter region code; resolve once per code.
const countryNames = new Map()

export const getCountryName = (countryCode) => {
    const code = typeof countryCode === 'string' ? countryCode.trim().toUpperCase() : ''
    if (!/^[A-Z]{2}$/.test(code)) return countryCode
    if (!countryNames.has(code)) {
        try {
            countryNames.set(code, new Intl.DisplayNames(['en'], {type: 'region'}).of(code))
        } catch (error) {
            console.warn('Error getting country name:', error)
            countryNames.set(code, countryCode)
        }
    }
    return countryNames.get(code)
}


/**
* Generates an array of React <meta> tag elements based on the provided tag values.
* If a tag value contains the placeholder '${OriginalRequestURL}', it is replaced with the current page's full URL.
*
* @param {Array<Object>} tagValues - An array of objects where each object represents a meta tag with a single key-value pair.
* @returns {Array<JSX.Element>} An array of React <meta> elements with the appropriate name and content attributes.
*/
export const generateMetaTags = (tagValues, props) => {
    if (!tagValues) return
    return tagValues.map((tagObj, idx) => {
        const [key, value] = Object.entries(tagObj)[0]
        return <meta key={idx} name={key} content={value} />
    })
}

/**
* Generates an array of React <link> elements for alternate URLs based on provided hreflang and url properties.
* Filters the input array to include only objects with valid hreflang and url values, then maps each to a <link> element
* with appropriate attributes for use in SEO and internationalization.
*
* @param {Array<Object>} c_alternateURLs - Array of alternate URL objects, each containing 'hreflang' and 'url' properties.
* @returns {Array<React.ReactElement>} Array of React <link> elements for alternate URLs, or an empty array if input is invalid or empty.
*/
export const generateAlternateUrls = (c_alternateURLs) => {
    return Array.isArray(c_alternateURLs) && c_alternateURLs
        .filter((alt) => alt?.hreflang && alt?.url)
        .map((alt) => (
            <link
                rel="alternate"
                hreflang={alt.hreflang}
                href={alt.url}
                key={`${alt.hreflang}-${alt.url}`}
            />
        )
    )
}

/**
 * This util func determines if the app is finished hydration
 * @return {boolean}
 */
export const isHydrated = () => typeof window !== 'undefined' && !window.__HYDRATING__

/**
 * Determines if the hostname represents a localhost environment.
 * Accepts an explicit hostname or falls back to window.location.hostname.
 * @param {string} [hostname]
 * @returns {boolean}
 */
export const isLocalhost = (hostname) => {
    const host =
        (hostname ||
            (typeof window !== 'undefined' ? window.location.hostname : '') ||
            '').toLowerCase()

    return (
        host === 'localhost' ||
        host.endsWith('.localhost') ||
        host === '127.0.0.1' ||
        host === '::1' ||
        host === '[::1]'
    )
}

// Defer script loading until browser is idle (after React hydration)
export function deferScriptLoading(callback) {
    if (typeof window === 'undefined') return

    if ('requestIdleCallback' in window) {
        window.requestIdleCallback(callback, {timeout: 3000})
    } else {
        setTimeout(callback, 1)
    }
}

/**
 * Builds a cross-SITE link to the equivalent of the current page in another region.
 * US and CA are separate SFCC sites (not locales of one site).
 *
 * Preference order:
 *   1. The current product's (or category's) `c_countrySelectorURLs`, which holds the full
 *      URL to the equivalent PRODUCT on the target site (so we land on the product, not the
 *      homepage).
 *   2. The country-selector config domain/path as a fallback.
 *
 * @param {Object} params
 * @param {string} params.targetRegion - Country code to resolve, e.g. 'US' or 'CA'.
 * @param {Object} [params.product] - Current product from header context.
 * @param {Object} [params.category] - Current category from header context.
 * @param {Object} [params.countrySelectorData] - Data from useCountrySelector().
 * @param {string} [params.brandId] - Current brand id (garage/dynamite).
 * @param {string} [params.pathname] - Current location pathname.
 * @returns {string|null} Absolute URL, or null when the target region isn't resolvable yet.
 */
export const buildCrossSiteLink = ({
    targetRegion,
    product,
    category,
    countrySelectorData,
    brandId,
    pathname = ''
}) => {
    // 1. Prefer the page-specific product/category URL for the target region.
    const pageUrls = product?.c_countrySelectorURLs || category?.c_countrySelectorURLs
    if (Array.isArray(pageUrls)) {
        const pageMatch = pageUrls.find(
            (entry) => entry?.locale?.split('_')[1]?.toUpperCase() === targetRegion
        )
        if (pageMatch?.url) return pageMatch.url
    }

    // 2. Fall back to the country-selector config (site root / mapped path).
    const config = countrySelectorData?.countrySelectorConfig
    if (!config) return null

    const targetKey = Object.keys(config).find(
        (key) => key.split('_')[1]?.toUpperCase() === targetRegion
    )
    if (!targetKey) return null

    const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    const isLocalEnv = hostname ? isLocalhost(hostname) : false

    if (isLocalEnv && LOCALHOST_COUNTRY_URLS[targetKey]) {
        return LOCALHOST_COUNTRY_URLS[targetKey].replace('localhost', hostname)
    }

    const selectedConfig = config[targetKey]
    const cleanPathname = pathname.startsWith('/') ? pathname.substring(1) : pathname
    const domainValue = selectedConfig?.domain
    const domain =
        (isLocalEnv
            ? origin
            : domainValue && typeof domainValue === 'object'
              ? domainValue[brandId]
              : domainValue) ||
        origin ||
        ''
    if (!domain) return null

    const path =
        countrySelectorData?.countrySelectorPages?.[cleanPathname]?.[targetKey] ??
        selectedConfig?.path ??
        cleanPathname
    const fullDomain = domain.startsWith('http') ? domain : `https://${domain}`
    const cleanDomain = fullDomain.replace(/\/$/, '')
    const cleanPath = path.startsWith('/') ? path : `/${path}`

    return `${cleanDomain}${cleanPath}`
}

